import moment from "moment";
import addCommonFormats from "ajv-formats";
import NP from "number-precision";

const addFormats = (ajv) => {
	addCommonFormats(ajv);
	ajv.addVocabulary(['example', 'x-uitype', 'x-uioptions', 'x-uidependencies', 'masterDataMappings', 'masterData', 'securityClassification', 'sensitivityClassification']);
	ajv.addFormat("custom:date-time:YYYY-MM-DD HH:mm:ss", {
		type: "string",
		validate: (x) => {
			return moment(x, "YYYY-MM-DD HH:mm:ss", true).isValid();
		},
	});
	ajv.addFormat("custom:date-time:YYYY-MM-DDTHH:mm:ssZ", {
		type: "string",
		validate: (x) => {
			return moment(x, "YYYY-MM-DDTHH:mm:ssZ", true).isValid();
		},
	});

	ajv.addFormat("custom:date-time:YYYY-MM", {
		type: "string",
		validate: (x) => {
			return moment(x, "YYYY-MM", true).isValid();
		},
	});

	ajv.addFormat("custom:date-only:YYYY-MM-DD", {
		type: "string",
		validate: (x) => {
			return moment(x, "YYYY-MM-DD", true).isValid();
		},
	});

	//To handle decimal precision correctly
	ajv.removeKeyword("multipleOf");
	ajv.addKeyword({
		keyword: "multipleOf",
		type: "number",
		compile(schema) {
			return (data) => Number.isInteger(NP.divide(data, schema));
		},
		errors: false,
		metaSchema: {
			type: "number",
		},
	});

	// Adding Business Logic validation rules
    // Sum of fields at root level and field inside an array of objects equals to given number
	ajv.addKeyword({
		keyword: 'sumEquals',
		type: 'object',
		schemaType: 'object',
		errors: true,
		validate: function validateSumEquals(schema, data) {
			const errors = [];
			const rootSum = (schema.fields || []).reduce(
				(acc, key) => acc + (data[key] || 0),
				0
			);
			const arraySum = Array.isArray(data[schema.arrayField])
				? data[schema.arrayField].reduce((acc, item) => {
					return acc + (item[schema.arrayFieldProp] || 0);
				}, 0)
				: 0;

			const total = rootSum + arraySum;
			const isValid = total === schema.total;
			if (!isValid) {
				// Add errors for each root-level field
				for (const field of schema.fields) {
					errors.push({
						keyword: 'sumEquals',
						instancePath: `/${field}`,
						schemaPath: '',
						params: { rootSum, arraySum, total, totalExpected: schema.total },
						message: `The total ratio must equal to ${schema.total}`,
					});
				}
	  			// Add errors for each array item
				if (Array.isArray(data[schema.arrayField])) {
					for (let i = 0; i < data[schema.arrayField].length; i++) {
						errors.push({
							keyword: 'sumEquals',
							instancePath: `/${schema.arrayField}/${i}/${schema.arrayFieldProp}`,
							schemaPath: '',
							params: { rootSum, arraySum, total, totalExpected: schema.total },
							message: `The total ratio must equal to ${schema.total}`,
						});
					}
				}
				validateSumEquals.errors = errors;
				return false;
			}
			return isValid;
		},
	});

	//  Validate dates and ensure they are in chronological order
	ajv.addKeyword({
		keyword: 'dateCompare',
		type: 'object',
		schemaType: 'array',
		errors: true,

		validate: function validateDateCompare(schema, data) {
			// Map technical operators to grammatically correct phrases
			const operatorPhrases = {
				"lessThan": "before",
				"lessThanOrEqual": "on or before",
				"greaterThan": "after",
				"greaterThanOrEqual": "on or after",
				"sameDate": "on the same day as"
			};

			// Helper function to get grammatically correct phrase for an operator
            const getOperatorPhrase = (operator) => operatorPhrases[operator] || operator;

			// Helper function to create error object
			const createDateCompareError = (rule, message, index=null) => ({
				keyword: 'dateCompare',
				instancePath: rule.rootField ? `/${rule.rootField}` : `${rule.arrayField}/${index}/${rule.before}`,
				schemaPath: '',
				params: {
					rootField: rule.rootField,
					itemField: rule.itemField,
					operator: getOperatorPhrase(rule.operator)
				},
				message
			});

			// Helper function to validate date comparison based on operator
			const validateComparison = (beforeField, afterField, operator) => {
				const beforeDate = moment.parseZone(beforeField);
				const afterDate = moment.parseZone(afterField);

				if (!beforeDate.isValid() || !afterDate.isValid()) {
					return false;
				}

				// Map operators to moment methods for cleaner code
				const operatorMap = {
					"lessThan": () => beforeDate.isBefore(afterDate),
					"lessThanOrEqual": () => beforeDate.isSameOrBefore(afterDate),
					"greaterThan": () => beforeDate.isAfter(afterDate),
					"greaterThanOrEqual": () => beforeDate.isSameOrAfter(afterDate),
					"sameDate": () => beforeDate.isSame(afterDate, 'day')
				};

				// Return false for unknown operators
				return operatorMap[operator] ? operatorMap[operator]() : false;
			};

			// Helper function to validate root level date comparison
			const validateRootComparison = (rule, data) => {
				const valid = validateComparison(data[rule.before], data[rule.after], rule.operator);
				console.log("valid", valid)
				if (!valid) {
					const phrase = getOperatorPhrase(rule.operator);
					validateDateCompare.errors = [createDateCompareError(
						rule, 
						`Date ${data[rule.before]} should be ${phrase} ${data[rule.after]}`
                    )];
				}
                return valid;
			};

			// Helper function to validate array level date comparison
			const validateArrayComparison = (rule, data) => {
				const arrayField = data[rule.arrayField];

				if (!Array.isArray(arrayField)) {
					validateDateCompare.errors = [createDateCompareError(
						rule,
						`Invalid rule configuration for date comparison`
					)];
					return false;
				}
				let index=0;
				validateDateCompare.errors=[];
				let error=false;
				for (const item of arrayField) {
					const valid = validateComparison(item[rule.before], item[rule.after], rule.operator);
					if (!valid) {
						const phrase = getOperatorPhrase(rule.operator);
						validateDateCompare.errors.push(createDateCompareError(
							rule,
							`Date ${item[rule.before]} should be ${phrase} ${item[rule.after]}`,
							index
						));
						error=true;
					}
					index++;
				}

				return !error;
			};

			// Main validation logic
			for (const rule of schema) {
				// Validate rule has a type
				if (!rule.type) {
					validateDateCompare.errors = [createDateCompareError(
						rule,
						`Invalid rule configuration for date comparison`
					)];
					return false;
				}

				// Process based on comparison type
				if (rule.type === "ROOT") {
					if (!validateRootComparison(rule, data)) {
						return false;
					}
				} else if (rule.type === "ARRAY") {
					if (!validateArrayComparison(rule, data)) {
						return false;
					}
				} else {
					// Unknown comparison type
					validateDateCompare.errors = [createDateCompareError(
						rule,
						`Unknown comparison type: ${rule.type}`
					)];
					return false;
				}
			}

			return true;
		},
	});

	// Validate same day at root level and fields inside array
	ajv.addKeyword({
		keyword: "sameDay",
		type: "object",
		schemaType: "object",
		errors: true,
		validate: function validateSameDay(schema, data) {
			const errors = [];
			const rootDateStr = data[schema.rootField];
			const rootDate = moment.parseZone(rootDateStr, 'YYYY-MM-DD', true);
			const array = data[schema.arrayField];

			if (!rootDate.isValid() || !Array.isArray(array)) return true;

			array.forEach((item, index) => {
				const itemDateStr = item[schema.itemField];
				const itemDate = moment.parseZone(itemDateStr);
				if (!itemDate.isValid() || !itemDate.isSame(rootDate, 'day')) {
					errors.push({
						keyword: 'sameDay',
						instancePath: `/${schema.rootField}`,
						schemaPath: '',
						params: {
							rootDate: rootDateStr,
							itemDate: itemDateStr,
							rootField: schema.rootField,
							itemField: schema.itemField
						},
						message: `Date of ${schema.itemField} must be same day as the ${schema.rootField}.`
					});
					errors.push({
						keyword: 'sameDay',
						instancePath: `/${schema.arrayField}/${index}/${schema.itemField}`,
						schemaPath: '',
						params: {
							rootDate: rootDateStr,
							itemDate: itemDateStr,
							rootField: schema.rootField,
							itemField: schema.itemField
						},
						message: `Date of ${schema.itemField} must be same day as the ${schema.rootField}.`
					});
				}
			});
			if (errors.length > 0) {
				validateSameDay.errors = errors;
				return false;
			}

			return true;
  		}
	});

	// Validate max day duration
	ajv.addKeyword({
		keyword: "maxTotalDuration",
		type: "object",
		schemaType: "object",
		errors: true,
		validate: function validateMaxTotalDuration(schema, data) {
			const array = data[schema.arrayField];
			const timeInField = schema.timeInField;
			const timeOutField = schema.timeOutField;
			const maxHours = schema.maxHours;

			if (!Array.isArray(array)) return true;

			let totalMinutes = 0;

			for (const entry of array) {
				const inTime = moment.parseZone(entry[timeInField]);
				const outTime = moment.parseZone(entry[timeOutField]);
				if (!inTime.isValid() || !outTime.isValid()) continue;

				const diffMinutes = outTime.diff(inTime, 'minutes');
				if (diffMinutes < 0) continue; // skip if invalid range

				totalMinutes += diffMinutes;
			}

			const totalHours = totalMinutes / 60;
			const valid = totalHours <= maxHours;

			if (!valid) {
				validateMaxTotalDuration.errors = [{
					keyword: 'maxTotalDuration',
					instancePath: `/${schema.arrayField}`,
					schemaPath: '',
					params: {
						totalHours,
						maxHours,
						arrayField: schema.arrayField
					},
					message: `Total hours within ${schema.arrayField} cannot exceed ${maxHours} hours`
				}];
			}

			return valid;
		}
	});

	ajv.addKeyword({
		keyword: 'masterDataValidation',
		type: 'object',
		schemaType: 'object',
		errors: true,
		validate: function validateMasterData(mappings, payload, fullSchema) {
			const masterData = fullSchema?.masterData;
			
			if (!mappings || Object.keys(mappings).length === 0) {
				return true;
			}

			if (!masterData) {
				validateMasterData.errors = [{
					keyword: 'masterDataValidation',
					instancePath: '',
					schemaPath: '',
					params: {},
					message: 'Master data mappings defined but master data is missing'
				}];
				return false;
			}

			const errors = [];

			for (const [fieldName, fieldConfig] of Object.entries(mappings)) {
				if (!fieldConfig.masterDataTableName || !fieldConfig.masterDataFieldName) {
					continue;
				}

				const validValues = getValidValuesFromMasterData(masterData, fieldConfig.masterDataTableName, fieldConfig.masterDataFieldName);

				if (validValues.size === 0) continue;

				if (isArrayField(fieldConfig)) {
					validateArrayField(payload, fieldName, fieldConfig, validValues, errors);
				} else {
					validateRootField(payload, fieldName, fieldConfig, validValues, errors);
				}
			}

			if (errors.length > 0) {
				validateMasterData.errors = errors;
				return false;
			}
			return true;


			function getValidValuesFromMasterData(masterData, tableName, fieldName) {
				const table = masterData[tableName];
				if (!Array.isArray(table)) return new Set();
				
				return new Set(
					table
						.map(row => row[fieldName])
						.filter(val => val != null && val !== '')
						.map(String)
				);
			}

			function isArrayField(config) {
				return config.payloadParentType === 'array' && config.payloadParentField;
			}

			function validateArrayField(payload, fieldName, config, validValues, errors) {
				const array = payload[config.payloadParentField];
				
				if (!Array.isArray(array)) return;

				array.forEach((item, index) => {
					if (!item || typeof item !== 'object') return;
					
					const value = item[fieldName];
					if (value == null || value === '') return;

					if (!validValues.has(String(value))) {
						errors.push({
							keyword: 'masterDataValidation',
							instancePath: `/${config.payloadParentField}/${index}/${fieldName}`,
							schemaPath: '',
							params: {
								field: fieldName,
								value: String(value),
								masterDataTable: config.masterDataTableName,
								masterDataField: config.masterDataFieldName
							},
							message: 'Value is not valid according to master data',
							data: value
						});
					}
				});
			}

			function validateRootField(payload, fieldName, config, validValues, errors) {
				const value = payload[fieldName];
				
				if (value == null || value === '') return;

				if (!validValues.has(String(value))) {
					errors.push({
						keyword: 'masterDataValidation',
						instancePath: `/${fieldName}`,
						schemaPath: '',
						params: {
							field: fieldName,
							value: String(value),
							masterDataTable: config.masterDataTableName,
							masterDataField: config.masterDataFieldName
						},
						message: 'Value is not valid according to master data',
						data: value
					});
				}
			}
		}
	});
};

export default addFormats;
